import { bindable, inject } from 'aurelia';

// Kunder vars formulär får namn på fälten som saknar ett. Införs kund för kund, som fältramarna i site.css.
const ENABLED_FOR = new Set(['0026']);

const CONTROLS = 'input:not([type=hidden]):not([type=file]):not([type=button]):not([type=submit]), select, textarea';

/**
 * Ger formulärfält ett namn som skärmläsare kan läsa upp (WCAG 1.3.1, 4.1.2), när mallen inte kopplar
 * fältet till sin etikett:
 * - i en .form-group pekar fältet på gruppens <label> med aria-labelledby, en kryssruta på etiketten efter sig
 * - i en tabell får fältet radens och kolumnens rubrik som aria-label, t.ex. "Monthly salary, Year 1"
 *
 * Fält som redan har ett namn lämnas orörda. Används på formulärets fieldset med kundens id:
 * <fieldset accessible-names.bind="customerId">
 */
@inject(Element)
export class AccessibleNamesCustomAttribute {
  @bindable value;

  constructor(element) {
    this.element = element;
    this.nextId = 0;
    this.scheduled = false;
    this.observer = null;
    this.onInput = () => this.schedule();
  }

  attached() {
    this.start();
  }

  valueChanged() {
    this.start();
  }

  detaching() {
    this.stop();
  }

  start() {
    this.stop();
    if (!ENABLED_FOR.has(this.value)) return;

    // Formuläret laddas in efter fieldset, och if/repeat lägger till och tar bort fält medan det fylls i.
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.element, { childList: true, subtree: true });
    // En radrubrik kan vara ett fält vars värde följer ett annat fält, t.ex. "(${form.lpk}%)".
    this.element.addEventListener('input', this.onInput);
    this.schedule();
  }

  stop() {
    this.observer?.disconnect();
    this.observer = null;
    this.element.removeEventListener('input', this.onInput);
  }

  schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    setTimeout(() => {
      this.scheduled = false;
      if (this.observer) this.nameControls();
    });
  }

  nameControls() {
    for (const control of this.element.querySelectorAll(CONTROLS)) {
      if (this.hasOwnName(control)) continue;

      const cell = control.closest('td, th');
      if (cell && this.element.contains(cell)) {
        const name = this.tableName(control, cell) || this.ownValue(control);
        if (name) this.setLabel(control, name);
        continue;
      }

      const found = this.groupLabel(control);
      const choice = this.placeholderOption(control);
      if (found && found.own) {
        control.setAttribute('aria-labelledby', this.idOf(found.label));
        control.dataset.accessibleName = 'labelledby';
      } else if (found) {
        // Etiketten hör till en yttre grupp med flera fält, t.ex. "Research disciplines (1–3)".
        this.setLabel(control, [this.text(found.label), choice].filter(Boolean).join(', '));
      } else {
        const name = choice || control.getAttribute('placeholder') || this.ownValue(control);
        if (name) this.setLabel(control, name);
      }
    }
  }

  // Ett namn som mallen gav fältet. Det som detta attribut satt tidigare räknas om, eftersom rubrikerna kan ändras.
  hasOwnName(control) {
    if (control.dataset.accessibleName) return false;
    return control.labels?.length > 0 || control.hasAttribute('aria-label') || control.hasAttribute('aria-labelledby');
  }

  setLabel(control, name) {
    if (control.getAttribute('aria-label') !== name) control.setAttribute('aria-label', name);
    control.dataset.accessibleName = 'label';
  }

  // Fältets etikett och om den hör till fältets egen grupp. Saknar gruppen etikett används närmaste yttre grupps.
  groupLabel(control) {
    if (control.type === 'checkbox' || control.type === 'radio') {
      const next = control.nextElementSibling;
      if (next?.tagName === 'LABEL' && !next.querySelector(CONTROLS)) return { label: next, own: true };
    }

    let own = true;
    for (let group = control.closest('.form-group'); group && this.element.contains(group); group = group.parentElement?.closest('.form-group')) {
      const label = [...group.querySelectorAll('label')].find(
        (candidate) => candidate.closest('.form-group') === group && !candidate.querySelector(CONTROLS) && this.text(candidate),
      );
      if (label) return { label, own };
      own = false;
    }
    return null;
  }

  // Texten i en rullistas tomma förval, t.ex. "--- Select main field ---" blir "Select main field".
  placeholderOption(control) {
    if (control.tagName !== 'SELECT') return '';
    const first = control.options[0];
    if (!first || first.value !== '') return '';
    return first.textContent.replace(/^[\s-]+|[\s-]+$/g, '').trim();
  }

  // Ett låst fält som bara visar en text, t.ex. en radrubrik, heter som sitt värde.
  ownValue(control) {
    if (!control.disabled && !control.readOnly) return '';
    return (control.value || '').trim();
  }

  text(element) {
    return element.textContent.replace(/\s+/g, ' ').trim();
  }

  tableName(control, cell) {
    const row = cell.parentElement;
    const table = cell.closest('table');
    if (!row || !table) return '';

    const column = this.columnHeader(table, cell.cellIndex);
    const first = row.cells[0];
    if (first !== cell) return [this.cellText(first), column].filter(Boolean).join(', ');

    // I radrubrikens cell: ett fält efter rubriken heter som den, rubriken själv som kolumnen.
    const fields = [...cell.querySelectorAll(CONTROLS)];
    const before = fields[fields.indexOf(control) - 1];
    return before ? before.value.trim() || column : column;
  }

  // Kolumnrubriken är cellen med samma index i den första raden som har <th>.
  columnHeader(table, index) {
    const headerRow = [...table.rows].find((row) => row.querySelector('th'));
    const header = headerRow?.cells[index];
    return header && header.tagName === 'TH' ? this.cellText(header) : '';
  }

  // En radrubrik är ofta ett låst textfält med rubriken som värde, annars cellens text.
  cellText(cell) {
    if (!cell) return '';
    const field = cell.querySelector('input:not([type=hidden]), select, textarea');
    if (field && field.tagName === 'SELECT') return field.selectedOptions[0]?.textContent.trim() ?? '';
    if (field) return field.value.trim();
    return this.text(cell);
  }

  idOf(label) {
    if (!label.id) label.id = `accessible-name-${++this.nextId}`;
    return label.id;
  }
}
